var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MatrixEvent } from "matrix-js-sdk";
import { event, lifecycle } from "vscode-lib";
const PEEK_POLL_TIMEOUT = 30 * 1000;
const PEEK_POLL_ERROR_TIMEOUT = 30 * 1000;
const DEFAULT_OPTIONS = {
    snapshotInterval: 30, // send a snapshot after 30 events
};
/**
 * A helper class to read messages from Matrix using a MatrixClient,
 * without relying on the sync protocol.
 */
export class MatrixReader extends lifecycle.Disposable {
    constructor(matrixClient, roomId, translator, opts = {}) {
        super();
        this.matrixClient = matrixClient;
        this.roomId = roomId;
        this.translator = translator;
        this.disposed = false;
        this.polling = false;
        this.messagesSinceSnapshot = 0;
        this._onEvents = this._register(new event.Emitter());
        this.onEvents = this._onEvents.event;
        /**
         * Only receives messages from rooms the user has joined, and after startClient() has been called
         * (i.e.: they're received via the sync API).
         *
         * At this moment, we only poll for events using the /events endpoint.
         * I.e. the Sync API should not be used (and startClient() should not be called).
         *
         * We do this because we don't want the MatrixClient to keep all events in memory.
         * For yjs, this is not necessary, as events are document updates which are accumulated in the yjs
         * document, so already stored there.
         *
         * In a later version, it might be more efficient to call the /sync API manually
         * (without relying on the Timeline / sync system in the matrix-js-sdk),
         * because it allows us to retrieve events for multiple rooms simultaneously, instead of
         * a seperate /events poll per room
         */
        this.matrixRoomListener = (_event, _room, _toStartOfTimeline) => {
            console.error("not expected; Room.timeline on MatrixClient");
            // (disable error when testing / developing e2ee support,
            // in that case startClient is necessary)
            throw new Error("unexpected, we don't use /sync calls for MatrixReader, startClient should not be used on the Matrix client");
        };
        this.opts = Object.assign(Object.assign({}, DEFAULT_OPTIONS), opts);
        // TODO: catch events for when room has been deleted or user has been kicked
        this.matrixClient.on("Room.timeline", this.matrixRoomListener);
    }
    /**
     * Handle incoming events to determine whether a snapshot message needs to be sent
     *
     * MatrixReader keeps an internal counter of messages received.
     * every opts.snapshotInterval messages, we send a snapshot of the entire document state.
     */
    processIncomingEventsForSnapshot(events) {
        let shouldSendSnapshot = false;
        for (let event of events) {
            if (this.translator.isUpdateEvent(event)) {
                if (event.room_id !== this.roomId) {
                    throw new Error("event received with invalid roomid");
                }
                this.messagesSinceSnapshot++;
                if (this.messagesSinceSnapshot % this.opts.snapshotInterval === 0 &&
                    event.user_id === this.matrixClient.credentials.userId) {
                    // We don't want multiple users send a snapshot at the same time,
                    // to prevent this, we have a simple (probably not fool-proof) "snapshot user election"
                    // system which says that the user who sent a message SNAPSHOT_INTERVAL events since
                    // the last snapshot is responsible for posting a new snapshot.
                    // In case a user fails to do so,
                    // we use % to make sure we retry this on the next SNAPSHOT_INTERVAL
                    shouldSendSnapshot = true;
                }
            }
            else if (this.translator.isSnapshotEvent(event)) {
                this.messagesSinceSnapshot = 0;
                shouldSendSnapshot = false;
            }
        }
        return shouldSendSnapshot;
    }
    decryptRawEventsIfNecessary(rawEvents) {
        return __awaiter(this, void 0, void 0, function* () {
            const events = yield Promise.all(rawEvents.map((event) => __awaiter(this, void 0, void 0, function* () {
                if (event.type === "m.room.encrypted") {
                    const decrypted = (yield this.matrixClient.crypto.decryptEvent(new MatrixEvent(event))).clearEvent;
                    return decrypted;
                }
                else {
                    return event;
                }
            })));
            return events;
        });
    }
    /**
     * Peek for new room events using the Matrix /events API (long-polling)
     * This function automatically keeps polling until MatrixReader.dispose() is called
     */
    peekPoll() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.latestToken) {
                throw new Error("polling but no pagination token");
            }
            if (this.disposed) {
                return;
            }
            try {
                this.pendingPollRequest = this.matrixClient.http.authedRequest(undefined, "GET", "/events", {
                    room_id: this.roomId,
                    timeout: PEEK_POLL_TIMEOUT,
                    from: this.latestToken,
                }, undefined, PEEK_POLL_TIMEOUT * 2);
                const results = yield this.pendingPollRequest;
                this.pendingPollRequest = undefined;
                if (this.disposed) {
                    return;
                }
                const events = yield this.decryptRawEventsIfNecessary(results.chunk);
                const shouldSendSnapshot = this.processIncomingEventsForSnapshot(events);
                if (events.length) {
                    this._onEvents.fire({ events: events, shouldSendSnapshot });
                }
                this.latestToken = results.end;
                this.peekPoll();
            }
            catch (e) {
                console.error("peek error", e);
                if (!this.disposed) {
                    this.pollRetryTimeout = setTimeout(() => this.peekPoll(), PEEK_POLL_ERROR_TIMEOUT);
                }
            }
        });
    }
    /**
     * Before starting polling, call getInitialDocumentUpdateEvents to get the history of events
     * when coming online.
     *
     * This methods paginates back until
     * - (a) all events in the room have been received. In that case we return all events.
     * - (b) it encounters a snapshot. In this case we return the snapshot event and all update events
     *        that occur after that latest snapshot
     *
     * (if typeFilter is set we retrieve all events of that type. TODO: can we deprecate this param?)
     */
    getInitialDocumentUpdateEvents(typeFilter) {
        return __awaiter(this, void 0, void 0, function* () {
            let ret = [];
            let token = "";
            let hasNextPage = true;
            let lastEventInSnapshot;
            while (hasNextPage) {
                const res = yield this.matrixClient.createMessagesRequest(this.roomId, token, 30, "b"
                // TODO: filter?
                );
                const events = yield this.decryptRawEventsIfNecessary(res.chunk);
                for (let event of events) {
                    if (typeFilter) {
                        if (event.type === typeFilter) {
                            ret.push(event);
                        }
                    }
                    else if (this.translator.isSnapshotEvent(event)) {
                        ret.push(event);
                        lastEventInSnapshot = event.content.last_event_id;
                    }
                    else if (this.translator.isUpdateEvent(event)) {
                        if (lastEventInSnapshot && lastEventInSnapshot === event.event_id) {
                            if (!this.latestToken) {
                                this.latestToken = res.start;
                            }
                            return ret.reverse();
                        }
                        this.messagesSinceSnapshot++;
                        ret.push(event);
                    }
                }
                token = res.end;
                if (!this.latestToken) {
                    this.latestToken = res.start;
                }
                hasNextPage = res.start !== res.end;
            }
            return ret.reverse();
        });
    }
    /**
     * Start polling the room for messages
     */
    startPolling() {
        if (this.polling) {
            throw new Error("already polling");
        }
        this.polling = true;
        this.peekPoll();
    }
    get isStarted() {
        return this.polling;
    }
    dispose() {
        this.disposed = true;
        super.dispose();
        if (this.pollRetryTimeout) {
            clearTimeout(this.pollRetryTimeout);
        }
        if (this.pendingPollRequest) {
            this.pendingPollRequest.abort();
        }
        this.matrixClient.off("Room.timeline", this.matrixRoomListener);
    }
}
//# sourceMappingURL=MatrixReader.js.map